import { useTranslation } from 'react-i18next';
import { useSearchBar } from '../hooks/useSearchBar';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { groupSearchOptionsIntoRows } from './searchBarUtils';
import CustomDatePicker from './CustomDatePicker';

const formatDateForSearch = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date;

    const parsedDate = date instanceof Date ? date : new Date(date);
    if (isNaN(parsedDate.getTime())) return '';

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Optional topSelect: rendered as a centered row on top (right under heading). { label?: string, value: string, onChange: (value) => void, options: { value: string, label: string }[] }
 *
 * Search option shape: { key, label, type?, row? }
 * - row: optional row index (0 = first row). When omitted, name/fathersName/surname/firstName go to row 0, others to row 1.
 */
const SearchBar = ({
    getData,
    searchOptions,
    searchQuery,
    searchType,
    setSearchQuery,
    setSearchType,
    handleSearchClick,
    sortBy,
    setpageNumber,
    showCompanySelect = false,
    companySelectLabel = null,
    showLevelSelect = false,
    showActiveSelect = false,
    showAdditionalContractSelect = false,
    showTerminatedSelect = false,
    companyOptions = [],
    selectedCompany,
    setSelectedCompany,
    levelOptions = [],
    selectedLevel,
    setSelectedLevel,
    levelsLoading = false,
    selectedActive,
    setSelectedActive,
    selectedAdditionalContract,
    setSelectedAdditionalContract,
    selectedTerminated,
    setSelectedTerminated,
    setSelectedCountry,
    showCountriesSelect = false,
    selectedCountry,
    countriesOptions = [],
    setCountriesOptions,
    showNomenclaturesSelect = false,
    selectedNomenclature,
    setSelectedNomenclature,
    nomenclaturesOptions = [],
    flexDirection = 'row',
    topSelect,
    ignoreAdminBranchFilter = false,
    onOrgUnitPickerOpen = null,
    orgUnitDisplayLabel = '',
}) => {
    const { t } = useTranslation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);

    const filteredCompanyOptions = useMemo(() => {
        if (ignoreAdminBranchFilter || !adminBranchId || !companyOptions || companyOptions.length === 0) {
            return companyOptions;
        }
        return companyOptions.filter(company => String(company.id) === String(adminBranchId));
    }, [companyOptions, adminBranchId, ignoreAdminBranchFilter]);

    const handleActiveChange = (e) => {
        setSelectedActive(e.target.value);
    };

    const handleAdditionalContractChange = (e) => {
        if (typeof setSelectedAdditionalContract === 'function') {
            setSelectedAdditionalContract(e.target.value);
        }
    };

    const handleTerminatedChange = (e) => {
        if (typeof setSelectedTerminated === 'function') {
            setSelectedTerminated(e.target.value);
        }
    };

    const {
        handleKeyDown,
        onSearchClick,
    } = useSearchBar({
        searchOptions,
        searchQuery,
        searchType,
        setSearchType,
        setSearchQuery,
        handleSearchClick,
        getData,
        sortBy,
        setpageNumber
    });

    const searchQueries = typeof searchQuery === 'string'
        ? { [searchType]: searchQuery }
        : searchQuery || {};

    const optionRows = useMemo(() => {
        const rows = groupSearchOptionsIntoRows(searchOptions || []);
        return rows.length > 0 ? rows : [[]];
    }, [searchOptions]);

    const useMultiRowLayout = optionRows.length > 1;

    const handleInputChange = (e, optionKey) => {
        if (typeof setSearchQuery !== 'function') return;
        const newQueries = {
            ...searchQueries,
            [optionKey]: e.target.value
        };
        setSearchQuery(newQueries);
    };

    const handleDateChange = (optionKey, date) => {
        if (typeof setSearchQuery !== 'function') return;
        setSearchQuery({
            ...searchQueries,
            [optionKey]: formatDateForSearch(date),
        });
    };

    const hasSearchOptions = (searchOptions?.length ?? 0) > 0;
    const hasExtraSelects = showCompanySelect || showLevelSelect || showCountriesSelect
        || showNomenclaturesSelect || showActiveSelect || showAdditionalContractSelect || showTerminatedSelect;
    const effectiveOptionRows = hasSearchOptions
        ? optionRows
        : (hasExtraSelects ? [[]] : []);

    const renderSearchOption = (option) => (
        <div key={option.key} className="search-option-container">
            {option.type === 'orgUnitPicker' && (
                <div className="search-option-container search-option-container--org-unit">
                    <input
                        type="text"
                        className="search-input"
                        readOnly
                        placeholder={t(`users.table.search.options.${option.label}`)}
                        value={orgUnitDisplayLabel || ''}
                        title={orgUnitDisplayLabel || ''}
                        onKeyDown={handleKeyDown}
                    />
                    <button
                        type="button"
                        className="form-btn btn-blue"
                        style={{ height: '30px' }}
                        onClick={() => onOrgUnitPickerOpen?.()}
                        title={t(`users.table.search.options.${option.label}`)}
                    >
                        ...
                    </button>
                </div>
            )}
            {option.type !== 'date' && option.type !== 'select' && option.type !== 'orgUnitPicker' && (
                <input
                    type="search"
                    className="search-input"
                    placeholder={t(`users.table.search.options.${option.label}`)}
                    value={searchQueries[option.key] || ''}
                    onChange={(e) => handleInputChange(e, option.key)}
                    onKeyDown={handleKeyDown}
                />
            )}
            {option.type === 'date' && (
                <div className="search-option-container search-option-container--labeled search-option-container--date">
                    <label className="label-absolute">
                        {t(`users.table.search.options.${option.label}`)}
                    </label>
                    <CustomDatePicker
                        name={option.key}
                        value={searchQueries[option.key] || ''}
                        onChange={(date) => handleDateChange(option.key, date)}
                        isSmall={true}
                        renderInPortal={true}
                    />
                </div>
            )}
            {option.type === 'select' && (
                <div className="search-option-container search-option-container--labeled">
                    <label className="label-absolute">
                        {t(`users.table.search.options.${option.label}`)}
                    </label>
                    <select
                        className="search-input"
                        value={searchQueries[option.key] ?? option.defaultValue ?? ''}
                        onChange={(e) => handleInputChange(e, option.key)}
                        onKeyDown={handleKeyDown}
                    >
                        <option value="">{t('common.select')}</option>
                        {(option.options || []).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.labelKey ? t(opt.labelKey) : opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );

    const renderExtraSelects = () => (
        <>
            {showCompanySelect && (
                <div className="search-option-container search-option-container--labeled">
                    <label className="label-absolute">
                        {companySelectLabel || t('users.table.search.options.company')}
                    </label>
                    <select
                        className="search-input"
                        value={selectedCompany || ''}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                    >
                        <option value="">{t('common.select')}</option>
                        {filteredCompanyOptions.map(company => (
                            <option key={company.id} value={company.id}>
                                {company.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {showLevelSelect && (
                <div className="search-option-container search-option-container--labeled">
                    <label
                        className="label-absolute"
                        style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            position: 'absolute',
                            top: '-8px',
                            left: '10px',
                            backgroundColor: 'white',
                            padding: '0 5px',
                            color: '#555',
                            zIndex: 1,
                        }}
                    >
                        {t('users.table.search.options.level')}
                    </label>
                    <select
                        className="search-input"
                        value={selectedLevel || ''}
                        onChange={(e) => {
                            if (typeof setSelectedLevel === 'function') {
                                setSelectedLevel(e.target.value);
                            }
                        }}
                        disabled={levelsLoading}
                    >
                        <option value="">
                            {levelsLoading ? t('common.loading') : t('common.select')}
                        </option>
                        {levelOptions.map(level => (
                            <option key={level.id} value={level.id}>{level.longDescription}</option>
                        ))}
                    </select>
                </div>
            )}
            {showCountriesSelect && (
                <div className="search-option-container">
                    <select className="search-input" value={selectedCountry || ''} onChange={(e) => setSelectedCountry(e.target.value)}>
                        <option value="">{t('users.table.search.options.country')}</option>
                        {countriesOptions.map(country => (
                            <option key={country.id} value={country.id}>{country.name}</option>
                        ))}
                    </select>
                </div>
            )}
            {showNomenclaturesSelect && (
                <div className="search-option-container">
                    <select className="search-input" value={selectedNomenclature || ''} onChange={(e) => setSelectedNomenclature(e.target.value)}>
                        <option value="">{t('users.table.search.options.nomenclature')}</option>
                        {nomenclaturesOptions.map(nomenclature => (
                            <option key={nomenclature.id} value={nomenclature.nomenclature.id}>{nomenclature.name}</option>
                        ))}
                    </select>
                </div>
            )}
            {showActiveSelect && (
                <div className="search-option-container">
                    <select className="search-input" value={selectedActive || 'true'} onChange={handleActiveChange}>
                        <option value="true">{t('users.table.search.options.active')}</option>
                        <option value="false">{t('users.table.search.options.unactive')}</option>
                    </select>
                </div>
            )}
            {showAdditionalContractSelect && (
                <div className="search-option-container search-option-container--additional-contract">
                    <select
                        className="search-input"
                        value={selectedAdditionalContract ?? 'false'}
                        onChange={handleAdditionalContractChange}
                        title={
                            selectedAdditionalContract === 'true'
                                ? t('staff.register.search.additionalContract')
                                : t('staff.register.search.mainContract')
                        }
                    >
                        <option value="false">{t('staff.register.search.mainContract')}</option>
                        <option value="true">{t('staff.register.search.additionalContract')}</option>
                    </select>
                </div>
            )}
            {showTerminatedSelect && (
                <div className="search-option-container search-option-container--terminated">
                    <select
                        className="search-input"
                        value={selectedTerminated ?? 'false'}
                        onChange={handleTerminatedChange}
                        title={
                            selectedTerminated === 'true'
                                ? t('staff.register.search.terminated')
                                : t('staff.register.search.working')
                        }
                    >
                        <option value="false">{t('staff.register.search.working')}</option>
                        <option value="true">{t('staff.register.search.terminated')}</option>
                    </select>
                </div>
            )}
        </>
    );

    const lastRowIndex = effectiveOptionRows.length - 1;

    return (
        <div className={`search-bar ${flexDirection === 'column' ? 'search-bar--column' : ''} ${useMultiRowLayout ? 'search-bar--multi-row' : ''}`}>
            {topSelect && (
                <div className="search-bar-top-row" style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                    {topSelect.label != null && topSelect.label !== '' && (
                        <label className="label-absolute" style={{ marginBottom: '4px', position: 'static', marginRight: '8px' }}>
                            {topSelect.label}
                        </label>
                    )}
                    <select
                        className="search-input"
                        style={{ minWidth: '320px', maxWidth: '600px', boxSizing: 'border-box' }}
                        value={topSelect.value ?? ''}
                        onChange={(e) => topSelect.onChange(e.target.value)}
                    >
                        {(topSelect.options || []).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {(hasSearchOptions || hasExtraSelects) && (
                <>
                    {hasSearchOptions && <label className="search-label">{t('users.table.search.label')}</label>}
                    <div className={useMultiRowLayout ? 'search-options-stacked' : 'search-options'}>
                        {effectiveOptionRows.map((rowOptions, rowIndex) => (
                            <div key={rowIndex} className="search-options-row">
                                {hasSearchOptions && rowOptions.map((option) => renderSearchOption(option))}
                                {rowIndex === lastRowIndex && renderExtraSelects()}
                                {rowIndex === lastRowIndex && hasSearchOptions && (
                                    <button
                                        type="button"
                                        className="search-button"
                                        onClick={onSearchClick}
                                    >
                                        {t('users.table.search.button')}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default SearchBar;
